import { useEffect, useMemo, useState } from "react";
import { Input, Form, Select, Row, Col } from "antd";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Updates, { ReleaseUpdateTypeOptions } from "../../Services/UpdatesService";
import { useForm } from "antd/es/form/Form";
import SAModal from "../../Common/Custom Components/Ant design/SAModal";

export interface UpdateDetailsProps {
    onDismiss: (confirmed: boolean) => void;
    record: any;
    type: "add" | "edit";
    defaultVersion: any;
}


const UpdateDetails = ({ onDismiss, record, type, defaultVersion }: UpdateDetailsProps) => {
    const [form]: any = useForm();
    const [loading, setLoading] = useState(false);

    const generatedVersion = useMemo(() => {
        if (type === "edit") return record?.version;

        const now = new Date();
        const yy = now.getFullYear() % 100;
        const mm = now.getMonth() + 1;

        const last = defaultVersion?.version;

        if (!last) return `${yy}.${mm}.1`;

        const [lastYY, lastMM, lastCount] = last.split(".").map(Number);

        if (lastYY === yy && lastMM === mm) {
            return `${yy}.${mm}.${(lastCount || 0) + 1}`;
        }

        return `${yy}.${mm}.1`;
    }, [type, record, defaultVersion]);

    useEffect(() => {
        if (type === "edit") {
            form.setFieldsValue({
                version: record?.version ?? "",
                type: record?.type ?? "",
                title: record?.title ?? "",
                description: record?.description ?? "",
                content: record?.content ?? "",
            });
        } else {
            form.setFieldsValue({
                version: generatedVersion,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type, record, generatedVersion]);

    const handleSave = () => {
        const id = type === "edit" ? record.id : "";
        setLoading(true);
        form.validateFields()
            .then((values: any) => {
                Updates.addUpdate(id, values).then((res) => {
                    setLoading(false);
                    onDismiss(res?.status ? true : false);

                })
            })
            .catch(() => setLoading(false));
    }

    return (
        <SAModal
            title={type === "edit" ? `Edit ${record?.title}` : "Add new update"}
            width={800}
            isLoading={loading}
            open={true}
            okText="Save"
            cancelText="Cancel"
            onOk={handleSave}
            onCancel={() => onDismiss(false)}
        >
            <Form form={form} requiredMark={false}>
                <Row gutter={[10, 10]}>
                    <Col xs={24} lg={12}>
                        <Form.Item
                            className="custom-form-item"
                            label="Version"
                            name="version"
                            labelCol={{ span: 24 }}
                            wrapperCol={{ span: 24 }}
                        >
                            <Input disabled className="line-height-28" />
                        </Form.Item>
                    </Col>
                    <Col xs={24} lg={12}>
                        <Form.Item
                            className="custom-form-item"
                            label="Type"
                            name="type"
                            labelCol={{ span: 24 }}
                            wrapperCol={{ span: 24 }}
                            rules={[{ required: true, message: "Select update type" }]}
                        >
                            <Select
                                className="select-height-28"
                                placeholder="type"
                                options={ReleaseUpdateTypeOptions}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[10, 10]}>
                    <Col span={24}>
                        <Form.Item
                            className="custom-form-item"
                            label="Title"
                            name="title"
                            labelCol={{ span: 24 }}
                            wrapperCol={{ span: 24 }}
                            rules={[{ required: true, message: "Title is required" }]}
                        >
                            <Input
                                placeholder="Title"
                                className="line-height-28"
                                onChange={(e) => {
                                    const value = e.target.value;
                                    const capitalized = value.charAt(0).toUpperCase() + value.slice(1);
                                    form.setFieldsValue({ title: capitalized });
                                }}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    className="custom-form-item"
                    label="Description"
                    name={["description"]}
                    labelCol={{ span: 24 }}
                    wrapperCol={{ span: 24 }}

                >
                    <div className="ckeditor-description">
                        <CKEditor
                            editor={ClassicEditor as any}
                            config={{
                                initialData:
                                    record?.description !== "" && record?.description !== null
                                        ? record?.description
                                        : form?.getFieldValue("description"),
                            }}
                            onChange={(e: any, editor: any) =>
                                form.setFieldsValue({ description: editor.getData() })
                            }
                        />
                    </div>
                </Form.Item>
                <div className="ckeditor-container">
                    <Form.Item
                        className="custom-form-item"
                        label="Content"
                        name="content"
                        labelCol={{ span: 24 }}
                        wrapperCol={{ span: 24 }}
                    >
                        <CKEditor
                            editor={ClassicEditor as any}
                            config={{
                                initialData:
                                    record?.content !== "" && record?.content !== null
                                        ? record?.content
                                        : form?.getFieldValue("content"),
                            }}
                            onChange={(e: any, editor: any) =>
                                form.setFieldsValue({ content: editor.getData() })
                            }
                        />
                    </Form.Item>
                </div>
            </Form>
        </SAModal>
    );
};

export default UpdateDetails;
